import { useEffect, useMemo, useState } from "react";
import {
    Plus,
    X,
    Trash2,
    Compass,
    Sparkles,
    ArrowRight,
    Search,
    Globe,
    MapPin,
    Mail,
    Phone,
    Linkedin,
    Activity,
    Calendar,
    Utensils,
    Presentation,
    Handshake,
    UserCircle,
    Star,
    Upload,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// Prospection Partenaires — independent from Réseau XV.
// Cards: identité + qualification + interlocuteurs + interactions.
// Pipeline: 7 statuses. Promotion → moves the card to Réseau XV.
// ─────────────────────────────────────────────────────────────

const STATUSES = [
    { v: "identifie", l: "Identifié" },
    { v: "a_contacter", l: "À contacter" },
    { v: "premier_echange", l: "Premier échange" },
    { v: "qualification", l: "Qualification en cours" },
    { v: "potentiel", l: "Partenaire potentiel" },
    { v: "valide", l: "Validé" },
    { v: "a_integrer", l: "Intégrer au Réseau XV" },
];

const TYPES = [
    { v: "banque_privee", l: "Banque privée" },
    { v: "banque_affaires", l: "Banque d'affaires" },
    { v: "family_office", l: "Family Office" },
    { v: "investisseur_prive", l: "Investisseur privé" },
    { v: "fonds_investissement", l: "Fonds d'investissement" },
    { v: "cgp", l: "CGP" },
    { v: "avocat", l: "Avocat" },
    { v: "expert_comptable", l: "Expert-comptable" },
    { v: "notaire", l: "Notaire" },
    { v: "fiscaliste", l: "Fiscaliste" },
    { v: "conseil_ma", l: "Conseil M&A" },
    { v: "immobilier", l: "Immobilier" },
    { v: "institutionnel", l: "Institutionnel" },
    { v: "relation_strategique", l: "Relation stratégique" },
    { v: "autre", l: "Autre" },
];

const INTERACTION_LABELS = {
    appel: "Appel",
    email: "Email",
    dejeuner: "Déjeuner",
    rdv: "Rendez-vous",
    conference: "Conférence",
    introduction: "Introduction",
};
const INTERACTION_ICONS = {
    appel: Phone,
    email: Mail,
    dejeuner: Utensils,
    rdv: Calendar,
    conference: Presentation,
    introduction: Handshake,
};

function typeLabel(v) {
    return TYPES.find((t) => t.v === v)?.l || v || "Autre";
}
function statusLabel(v) {
    return STATUSES.find((s) => s.v === v)?.l || v;
}

const EMPTY = {
    name: "",
    type: "autre",
    website: "",
    city: "",
    country: "",
    status: "identifie",
    sectors: [],
    ticket_band: "",
    geographies: [],
    interest_level: "",
    compatibility: 3,
    description: "",
    rationale: "",
    notes: "",
};

export default function CockpitProspects() {
    const [items, setItems] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [aiOpen, setAiOpen] = useState(false);
    const [view, setView] = useState("pipeline"); // pipeline | list

    const load = async () => {
        const [list, dash] = await Promise.all([
            cockpit.get("/prospects").then((r) => r.data),
            cockpit
                .get("/prospects/dashboard")
                .then((r) => r.data)
                .catch(() => null),
        ]);
        setItems(list);
        setDashboard(dash);
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        return items.filter((p) => {
            if (statusFilter && p.status !== statusFilter) return false;
            if (!filter) return true;
            const q = filter.toLowerCase();
            const hay = [
                p.name, p.city, p.country, p.website,
                typeLabel(p.type),
                ...(p.interlocutors || []).map((i) => i.name),
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [items, filter, statusFilter]);

    const seedDefaults = async () => {
        if (!window.confirm("Insérer les 57 fiches de départ ? (idempotent)"))
            return;
        const { data } = await cockpit.post("/prospects/seed-defaults");
        alert(`${data.created} fiches créées.`);
        load();
    };

    return (
        <div
            data-testid="cockpit-prospects"
            className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14"
        >
            {/* HEADER */}
            <div className="flex flex-wrap items-end justify-between mb-10 gap-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Pépinière · Pré-Réseau XV
                    </div>
                    <h1
                        className="font-serif text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.08] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Prospection Partenaires.
                    </h1>
                    <p className="mt-3 text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                        Veille, qualification et développement du réseau
                        stratégique. Cet espace fonctionne comme une
                        pépinière de relations avant intégration éventuelle
                        au Réseau XV.
                    </p>
                </div>
                <div className="flex gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setAiOpen(true)}
                        data-testid="prospects-ai-button"
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Sparkles strokeWidth={1.2} size={13} />
                        Recherche assistée
                    </button>
                    <button
                        type="button"
                        onClick={() => setEditing({ ...EMPTY })}
                        data-testid="prospects-new"
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-white/20 text-[#f4ecd8] text-[11px] tracking-[0.3em] uppercase hover:bg-white/[0.04] transition-all duration-500"
                    >
                        <Plus strokeWidth={1.2} size={13} />
                        Nouveau prospect
                    </button>
                </div>
            </div>

            {/* DASHBOARD */}
            {dashboard && (
                <section
                    data-testid="prospects-dashboard"
                    className="mb-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06]"
                >
                    <KpiCell
                        label="Identifiés"
                        value={dashboard.kpis.identified}
                    />
                    <KpiCell
                        label="Contactés"
                        value={dashboard.kpis.contacted}
                    />
                    <KpiCell
                        label="En qualification"
                        value={dashboard.kpis.in_qualification}
                    />
                    <KpiCell
                        label="Prêts pour Réseau XV"
                        value={dashboard.kpis.ready_to_promote}
                        accent
                    />
                </section>
            )}

            {/* PIPELINE STRIP */}
            {dashboard && (
                <section
                    data-testid="prospects-pipeline-strip"
                    className="mb-10 flex items-stretch overflow-x-auto border-y border-white/[0.06]"
                >
                    {dashboard.by_status.map((s) => {
                        const active = statusFilter === s.value;
                        return (
                            <button
                                key={s.value}
                                type="button"
                                data-testid={`pipeline-${s.value}`}
                                onClick={() =>
                                    setStatusFilter(active ? "" : s.value)
                                }
                                className={`min-w-[160px] flex-1 px-4 py-5 text-left border-r border-white/[0.06] last:border-r-0 transition-colors duration-500 ${
                                    active
                                        ? "bg-[#d4af37]/10 text-[#d4af37]"
                                        : "text-[#f4ecd8]/85 hover:bg-white/[0.02]"
                                }`}
                            >
                                <div className="text-[10px] tracking-[0.26em] uppercase mb-2 opacity-75">
                                    {s.label}
                                </div>
                                <div
                                    className="font-serif text-[24px] leading-none"
                                    style={{ fontWeight: 300 }}
                                >
                                    {s.count}
                                </div>
                            </button>
                        );
                    })}
                </section>
            )}

            {/* CONTROLS */}
            <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
                <div className="inline-flex border border-white/15">
                    {[
                        { v: "pipeline", l: "Pipeline" },
                        { v: "list", l: "Liste" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => setView(opt.v)}
                            data-testid={`prospects-view-${opt.v}`}
                            className={`px-3.5 py-1.5 text-[10px] tracking-[0.26em] uppercase transition-all duration-500 ${
                                view === opt.v
                                    ? "bg-[#d4af37]/15 text-[#d4af37]"
                                    : "text-[#f4ecd8]/55 hover:text-[#f4ecd8]"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    <Search strokeWidth={1.2} size={14} className="text-[#f4ecd8]/40" />
                    <input
                        type="text"
                        placeholder="Filtrer…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none px-2 py-1.5 text-[13px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/30 w-56"
                    />
                </div>
            </div>

            {/* BODY */}
            {items.length === 0 ? (
                <div className="py-20 text-center">
                    <Compass
                        strokeWidth={0.7}
                        size={42}
                        className="text-[#d4af37]/40 mx-auto mb-6"
                    />
                    <p className="text-[#f4ecd8]/55 text-[14px] mb-6">
                        Aucun prospect dans la pépinière.
                    </p>
                    <button
                        type="button"
                        onClick={seedDefaults}
                        data-testid="prospects-seed"
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Upload strokeWidth={1.2} size={13} />
                        Insérer la base de départ (57 structures)
                    </button>
                </div>
            ) : view === "pipeline" ? (
                <PipelineBoard
                    items={filtered}
                    onSelect={(p) => setEditing(p)}
                />
            ) : (
                <ProspectList
                    items={filtered}
                    onSelect={(p) => setEditing(p)}
                />
            )}

            {editing && (
                <ProspectEditor
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}

            {aiOpen && (
                <AiResearchModal
                    onClose={() => setAiOpen(false)}
                    onAdopted={() => {
                        setAiOpen(false);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function KpiCell({ label, value, accent }) {
    return (
        <div
            className={`bg-[#070d18] p-6 ${accent ? "border-l-2 border-[#d4af37]" : ""}`}
        >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/45 mb-2">
                {label}
            </div>
            <div
                className={`font-serif text-[34px] leading-none ${
                    accent ? "text-[#d4af37]" : "text-[#f4ecd8]"
                }`}
                style={{ fontWeight: 300 }}
            >
                {value ?? 0}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Pipeline columns (kanban style, sober)
// ─────────────────────────────────────────────────────────────
function PipelineBoard({ items, onSelect }) {
    const groups = useMemo(() => {
        const g = {};
        for (const s of STATUSES) g[s.v] = [];
        for (const p of items) {
            if (g[p.status]) g[p.status].push(p);
            else g[p.status] = [p];
        }
        return g;
    }, [items]);

    return (
        <div
            data-testid="prospects-pipeline"
            className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-7 gap-4 items-start"
        >
            {STATUSES.map((s) => (
                <div
                    key={s.v}
                    data-testid={`pipeline-col-${s.v}`}
                    className="bg-white/[0.02] border border-white/[0.06] p-4 min-h-[200px]"
                >
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#d4af37]/75 mb-4">
                        {s.l}
                        <span className="ml-2 text-[#f4ecd8]/40">
                            {groups[s.v].length}
                        </span>
                    </div>
                    <ul className="space-y-2">
                        {groups[s.v].map((p) => (
                            <li key={p.id}>
                                <button
                                    type="button"
                                    onClick={() => onSelect(p)}
                                    data-testid={`prospect-card-${p.id}`}
                                    className="w-full text-left p-3 border border-white/[0.07] hover:border-[#d4af37]/40 hover:bg-white/[0.03] transition-all duration-500"
                                >
                                    <div
                                        className="font-serif text-[13.5px] text-[#f4ecd8] leading-tight"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {p.name}
                                    </div>
                                    <div className="mt-1 text-[10.5px] text-[#f4ecd8]/45 tracking-wide">
                                        {typeLabel(p.type)}
                                        {p.country ? ` · ${p.country}` : ""}
                                    </div>
                                </button>
                            </li>
                        ))}
                        {groups[s.v].length === 0 && (
                            <li className="text-[11px] text-[#f4ecd8]/30 italic font-light">
                                —
                            </li>
                        )}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function ProspectList({ items, onSelect }) {
    return (
        <ul
            data-testid="prospects-list"
            className="border-t border-white/[0.06]"
        >
            {items.map((p) => (
                <li key={p.id} className="border-b border-white/[0.06]">
                    <button
                        type="button"
                        onClick={() => onSelect(p)}
                        data-testid={`prospect-row-${p.id}`}
                        className="w-full grid grid-cols-[1fr_auto_auto] items-center gap-6 py-4 px-2 hover:bg-white/[0.02] transition-colors duration-500 text-left"
                    >
                        <div className="min-w-0">
                            <div
                                className="font-serif text-[17px] text-[#f4ecd8] leading-tight truncate"
                                style={{ fontWeight: 400 }}
                            >
                                {p.name}
                            </div>
                            <div className="mt-1 text-[11.5px] text-[#f4ecd8]/45 tracking-wide truncate">
                                {typeLabel(p.type)}
                                {p.country ? ` · ${p.country}` : ""}
                                {p.city ? ` (${p.city})` : ""}
                            </div>
                        </div>
                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/75">
                            {statusLabel(p.status)}
                        </span>
                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/50">
                            {(p.interlocutors || []).length} int.
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    );
}

// ─────────────────────────────────────────────────────────────
// Prospect editor — 2 tabs (Identité / Suivi)
// ─────────────────────────────────────────────────────────────
function ProspectEditor({ initial, onClose, onSaved }) {
    const isNew = !initial.id;
    const [tab, setTab] = useState("identity");
    const [form, setForm] = useState({
        ...initial,
        sectors: (initial.sectors || []).join(", "),
        geographies: (initial.geographies || []).join(", "),
    });
    const [doc, setDoc] = useState(initial); // server copy with id
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e?.preventDefault?.();
        setBusy(true);
        setErr("");
        try {
            const payload = {
                name: form.name,
                type: form.type,
                website: form.website,
                city: form.city,
                country: form.country,
                status: form.status,
                sectors: (form.sectors || "")
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                ticket_band: form.ticket_band,
                geographies: (form.geographies || "")
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                interest_level: form.interest_level,
                compatibility: Number(form.compatibility) || 3,
                description: form.description,
                rationale: form.rationale,
                notes: form.notes,
            };
            let updated;
            if (isNew) {
                ({ data: updated } = await cockpit.post(
                    "/prospects",
                    payload,
                ));
                setDoc(updated);
                setTab("suivi");
            } else {
                ({ data: updated } = await cockpit.put(
                    `/prospects/${doc.id}`,
                    payload,
                ));
                setDoc(updated);
                onSaved();
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer "${doc.name}" de la pépinière ?`))
            return;
        await cockpit.delete(`/prospects/${doc.id}`);
        onSaved();
    };

    const promote = async () => {
        if (
            !window.confirm(
                `Intégrer "${doc.name}" au Réseau XV ?\nLa fiche sera transférée et retirée de la pépinière.`,
            )
        )
            return;
        try {
            await cockpit.post(`/prospects/${doc.id}/promote`);
            onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Promotion impossible");
        }
    };

    return (
        <div
            data-testid="prospect-modal"
            className="fixed inset-0 z-50 bg-[#070d18]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[920px] mt-8 mb-20 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500 z-10"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>
                <div className="px-10 lg:px-14 pt-10 pb-6 border-b border-white/[0.06]">
                    <div className="overline text-[#d4af37]/80 mb-3">
                        {isNew ? "Nouveau prospect" : statusLabel(doc.status)}
                    </div>
                    <h2
                        className="font-serif text-[26px] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        {isNew ? "Ajouter à la pépinière." : doc.name}
                    </h2>
                    {!isNew && (
                        <div className="mt-6 inline-flex border border-white/15">
                            {[
                                { v: "identity", l: "Identité & qualif." },
                                {
                                    v: "suivi",
                                    l: `Suivi · ${(doc.interlocutors || []).length} int. · ${(doc.interactions || []).length} échanges`,
                                },
                            ].map((t) => (
                                <button
                                    key={t.v}
                                    type="button"
                                    onClick={() => setTab(t.v)}
                                    data-testid={`prospect-tab-${t.v}`}
                                    className={`px-4 py-2 text-[10px] tracking-[0.24em] uppercase transition-all duration-500 ${
                                        tab === t.v
                                            ? "bg-[#d4af37]/15 text-[#d4af37]"
                                            : "text-[#f4ecd8]/55 hover:text-[#f4ecd8]"
                                    }`}
                                >
                                    {t.l}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-10 lg:px-14 py-8">
                    {tab === "identity" ? (
                        <IdentityForm
                            form={form}
                            onChange={onChange}
                            setForm={setForm}
                            submit={submit}
                            busy={busy}
                            err={err}
                            isNew={isNew}
                            doc={doc}
                            onClose={onClose}
                            onRemove={remove}
                            onPromote={promote}
                        />
                    ) : (
                        <SuiviTab
                            doc={doc}
                            onUpdated={setDoc}
                            onClose={onSaved}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function IdentityForm({
    form, onChange, setForm, submit, busy, err, isNew, doc, onClose,
    onRemove, onPromote,
}) {
    return (
        <form onSubmit={submit} className="space-y-6">
            <FieldRow
                label="Nom de la structure *"
                value={form.name}
                onChange={onChange("name")}
                required
                testid="prospect-name"
            />
            <div className="grid sm:grid-cols-2 gap-6">
                <FieldSelect
                    label="Type de structure"
                    value={form.type}
                    onChange={onChange("type")}
                    options={TYPES}
                />
                <FieldSelect
                    label="Statut"
                    value={form.status}
                    onChange={onChange("status")}
                    options={STATUSES}
                />
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
                <FieldRow
                    label="Site internet"
                    value={form.website}
                    onChange={onChange("website")}
                />
                <FieldRow
                    label="Ville"
                    value={form.city}
                    onChange={onChange("city")}
                />
                <FieldRow
                    label="Pays"
                    value={form.country}
                    onChange={onChange("country")}
                />
            </div>
            <FieldRow
                label="Secteurs privilégiés (séparés par virgule)"
                value={form.sectors}
                onChange={onChange("sectors")}
            />
            <div className="grid sm:grid-cols-2 gap-6">
                <FieldRow
                    label="Ticket habituel"
                    value={form.ticket_band}
                    onChange={onChange("ticket_band")}
                />
                <FieldRow
                    label="Zones géographiques (séparées par virgule)"
                    value={form.geographies}
                    onChange={onChange("geographies")}
                />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
                <FieldRow
                    label="Niveau d'intérêt potentiel"
                    value={form.interest_level}
                    onChange={onChange("interest_level")}
                />
                <div>
                    <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                        Compatibilité XV (1-5)
                    </label>
                    <input
                        type="number"
                        min={1}
                        max={5}
                        value={form.compatibility}
                        onChange={onChange("compatibility")}
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8]"
                    />
                </div>
            </div>
            <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                    Description
                </label>
                <textarea
                    rows={3}
                    value={form.description}
                    onChange={onChange("description")}
                    className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                />
            </div>
            <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                    Pertinence pour XV
                </label>
                <textarea
                    rows={3}
                    value={form.rationale}
                    onChange={onChange("rationale")}
                    className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                />
            </div>
            <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                    Notes privées
                </label>
                <textarea
                    rows={3}
                    value={form.notes}
                    onChange={onChange("notes")}
                    className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                />
            </div>

            {err && (
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                    {err}
                </div>
            )}

            <div className="pt-2 flex items-center justify-between flex-wrap gap-4">
                {!isNew ? (
                    <div className="flex items-center gap-5">
                        <button
                            type="button"
                            onClick={onRemove}
                            data-testid="prospect-delete"
                            className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#e89090]/80 hover:text-[#e89090] transition-colors duration-500"
                        >
                            <Trash2 strokeWidth={1.2} size={12} />
                            Supprimer
                        </button>
                        <button
                            type="button"
                            onClick={onPromote}
                            data-testid="prospect-promote"
                            className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#d4af37] hover:text-[#f4ecd8] transition-colors duration-500"
                        >
                            <ArrowRight strokeWidth={1.2} size={12} />
                            Intégrer au Réseau XV
                        </button>
                    </div>
                ) : (
                    <span />
                )}
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                    >
                        Fermer
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="prospect-save"
                        className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        {busy
                            ? "Enregistrement…"
                            : isNew
                              ? "Continuer →"
                              : "Mettre à jour"}
                    </button>
                </div>
            </div>
        </form>
    );
}

function FieldRow({ label, value, onChange, required, testid }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                {label}
            </label>
            <input
                type="text"
                required={required}
                data-testid={testid}
                value={value || ""}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8]"
            />
        </div>
    );
}

function FieldSelect({ label, value, onChange, options }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                {label}
            </label>
            <select
                value={value || ""}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8]"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v} className="bg-[#0b1220]">
                        {o.l}
                    </option>
                ))}
            </select>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Suivi tab — interlocuteurs + interactions
// ─────────────────────────────────────────────────────────────
function SuiviTab({ doc, onUpdated, onClose }) {
    const [list, setList] = useState(doc.interlocutors || []);
    const [busy, setBusy] = useState(false);

    const persistInterlocutors = async (next) => {
        setBusy(true);
        try {
            const { data } = await cockpit.put(
                `/prospects/${doc.id}/interlocutors`,
                { interlocutors: next },
            );
            setList(data.interlocutors || []);
            onUpdated?.(data);
        } finally {
            setBusy(false);
        }
    };

    const addInterlocutor = () => {
        const next = [
            ...list,
            {
                id: `new-${Date.now()}`,
                name: "",
                role_title: "",
                email: "",
                phone: "",
                linkedin: "",
                city: "",
                influence: 3,
                notes: "",
            },
        ];
        setList(next);
    };

    const removeInterlocutor = (id) => {
        if (!window.confirm("Retirer cet interlocuteur ?")) return;
        const next = list.filter((i) => i.id !== id);
        setList(next);
        persistInterlocutors(next);
    };

    const updateLocal = (id, patch) =>
        setList((prev) =>
            prev.map((i) => (i.id === id ? { ...i, ...patch } : i)),
        );

    return (
        <div data-testid="prospect-suivi-tab" className="space-y-10">
            {/* Interlocuteurs */}
            <div>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-1">
                            <UserCircle
                                strokeWidth={1}
                                size={12}
                                className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                            />
                            Interlocuteurs
                        </div>
                        <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light">
                            Personnes identifiées chez ce prospect.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={addInterlocutor}
                        data-testid="prospect-interlocutor-add"
                        className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Plus strokeWidth={1.2} size={12} />
                        Ajouter
                    </button>
                </div>
                <ul className="space-y-2 mb-3">
                    {list.map((it) => (
                        <ProspectInterlocutorRow
                            key={it.id}
                            it={it}
                            onChange={(p) => updateLocal(it.id, p)}
                            onRemove={() => removeInterlocutor(it.id)}
                        />
                    ))}
                    {list.length === 0 && (
                        <li className="text-[12px] text-[#f4ecd8]/40 italic font-light py-2">
                            Aucun interlocuteur.
                        </li>
                    )}
                </ul>
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={() => persistInterlocutors(list)}
                        disabled={busy}
                        data-testid="prospect-interlocutors-save"
                        className="px-5 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        Enregistrer les interlocuteurs
                    </button>
                </div>
            </div>

            {/* Interactions */}
            <InteractionsBlock
                doc={doc}
                onUpdated={onUpdated}
            />

            <div className="flex justify-end pt-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                >
                    Fermer
                </button>
            </div>
        </div>
    );
}

function ProspectInterlocutorRow({ it, onChange, onRemove }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <li
            className="border border-white/[0.06]"
            data-testid={`prospect-interlocutor-${it.id}`}
        >
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full grid grid-cols-[1fr_auto] items-center gap-4 py-3 px-4 hover:bg-white/[0.02] text-left transition-colors duration-500"
            >
                <div className="min-w-0">
                    <div
                        className="font-serif text-[15px] text-[#f4ecd8] leading-tight truncate"
                        style={{ fontWeight: 400 }}
                    >
                        {it.name || "Nouvel interlocuteur"}
                    </div>
                    <div className="mt-0.5 text-[11.5px] text-[#f4ecd8]/45 truncate">
                        {it.role_title || "—"}
                        {it.city ? ` · ${it.city}` : ""}
                    </div>
                </div>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/70 inline-flex items-center gap-1">
                    <Star strokeWidth={1.2} size={10} />
                    {it.influence || 3}/5
                </span>
            </button>
            {expanded && (
                <div className="px-4 pb-4 pt-2 space-y-4 border-t border-white/[0.04]">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <MicroField
                            label="Nom complet"
                            value={it.name}
                            onChange={(v) => onChange({ name: v })}
                        />
                        <MicroField
                            label="Fonction"
                            value={it.role_title}
                            onChange={(v) => onChange({ role_title: v })}
                        />
                        <MicroField
                            label="Email"
                            value={it.email}
                            onChange={(v) => onChange({ email: v })}
                        />
                        <MicroField
                            label="Téléphone"
                            value={it.phone}
                            onChange={(v) => onChange({ phone: v })}
                        />
                        <MicroField
                            label="LinkedIn"
                            value={it.linkedin}
                            onChange={(v) => onChange({ linkedin: v })}
                        />
                        <MicroField
                            label="Ville"
                            value={it.city}
                            onChange={(v) => onChange({ city: v })}
                        />
                        <div>
                            <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/75 mb-2">
                                Niveau d'influence (1-5)
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={5}
                                value={it.influence || 3}
                                onChange={(e) =>
                                    onChange({
                                        influence: Number(e.target.value) || 3,
                                    })
                                }
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13.5px] font-serif text-[#f4ecd8]"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/75 mb-2">
                            Notes privées
                        </label>
                        <textarea
                            value={it.notes || ""}
                            onChange={(e) => onChange({ notes: e.target.value })}
                            rows={2}
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[13.5px] font-serif text-[#f4ecd8] resize-y"
                        />
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onRemove}
                            className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#e89090]/80 hover:text-[#e89090] transition-colors duration-500"
                        >
                            <Trash2 strokeWidth={1.2} size={11} />
                            Retirer
                        </button>
                    </div>
                </div>
            )}
        </li>
    );
}

function MicroField({ label, value, onChange }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/75 mb-2">
                {label}
            </label>
            <input
                type="text"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13.5px] font-serif text-[#f4ecd8]"
            />
        </div>
    );
}

function InteractionsBlock({ doc, onUpdated }) {
    const [kind, setKind] = useState("appel");
    const [date, setDate] = useState(() =>
        new Date().toISOString().slice(0, 10),
    );
    const [note, setNote] = useState("");
    const [interlocutorId, setInterlocutorId] = useState("");
    const [busy, setBusy] = useState(false);

    const add = async () => {
        setBusy(true);
        try {
            const { data } = await cockpit.post(
                `/prospects/${doc.id}/interactions`,
                {
                    kind,
                    date,
                    note,
                    interlocutor_id: interlocutorId || null,
                },
            );
            setNote("");
            onUpdated?.(data);
        } finally {
            setBusy(false);
        }
    };
    const remove = async (aid) => {
        if (!window.confirm("Retirer cette interaction ?")) return;
        const { data } = await cockpit.delete(
            `/prospects/${doc.id}/interactions/${aid}`,
        );
        onUpdated?.(data);
    };

    const interlocutors = doc.interlocutors || [];
    const interactions = doc.interactions || [];

    return (
        <div data-testid="prospect-interactions">
            <div className="overline text-[#d4af37]/70 mb-4">
                <Activity
                    strokeWidth={1}
                    size={12}
                    className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                />
                Suivi relationnel
            </div>
            <div className="grid sm:grid-cols-[auto_1fr_2fr_1fr_auto] gap-3 items-end mb-5">
                <div>
                    <label className="block text-[9.5px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-1.5">
                        Type
                    </label>
                    <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        data-testid="prospect-interaction-kind"
                        className="bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif text-[#f4ecd8]"
                    >
                        {Object.entries(INTERACTION_LABELS).map(([v, l]) => (
                            <option key={v} value={v} className="bg-[#0b1220]">
                                {l}
                            </option>
                        ))}
                    </select>
                </div>
                <MicroField label="Date" value={date} onChange={setDate} />
                <MicroField
                    label="Compte rendu"
                    value={note}
                    onChange={setNote}
                />
                <div>
                    <label className="block text-[9.5px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-1.5">
                        Interlocuteur
                    </label>
                    <select
                        value={interlocutorId}
                        onChange={(e) => setInterlocutorId(e.target.value)}
                        className="bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif text-[#f4ecd8] w-full"
                    >
                        <option value="" className="bg-[#0b1220]">—</option>
                        {interlocutors.map((it) => (
                            <option
                                key={it.id}
                                value={it.id}
                                className="bg-[#0b1220]"
                            >
                                {it.name || "Sans nom"}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    type="button"
                    onClick={add}
                    disabled={busy}
                    data-testid="prospect-interaction-add"
                    className="px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    Ajouter
                </button>
            </div>
            <ul className="space-y-2">
                {interactions.map((a) => {
                    const Icon = INTERACTION_ICONS[a.kind] || Activity;
                    const it = interlocutors.find(
                        (i) => i.id === a.interlocutor_id,
                    );
                    return (
                        <li
                            key={a.id}
                            data-testid={`prospect-interaction-${a.id}`}
                            className="group flex items-start gap-3 py-2 border-b border-white/[0.04] last:border-b-0"
                        >
                            <Icon
                                strokeWidth={1.1}
                                size={12}
                                className="text-[#d4af37] mt-1 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 text-[10px] tracking-[0.24em] uppercase">
                                    <span className="text-[#d4af37]/85">
                                        {INTERACTION_LABELS[a.kind] || a.kind}
                                    </span>
                                    <span className="text-[#f4ecd8]/45">
                                        {a.date}
                                    </span>
                                    {it && (
                                        <span className="text-[#f4ecd8]/55">
                                            · {it.name}
                                        </span>
                                    )}
                                </div>
                                {a.note && (
                                    <p className="mt-1 text-[12.5px] font-serif text-[#f4ecd8]/80 leading-relaxed">
                                        {a.note}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(a.id)}
                                className="opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition mt-1"
                            >
                                <X strokeWidth={1.3} size={12} />
                            </button>
                        </li>
                    );
                })}
                {interactions.length === 0 && (
                    <li className="text-[11.5px] text-[#f4ecd8]/35 italic font-light">
                        Aucune interaction enregistrée.
                    </li>
                )}
            </ul>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// AI Research modal
// ─────────────────────────────────────────────────────────────
function AiResearchModal({ onClose, onAdopted }) {
    const [query, setQuery] = useState("");
    const [count, setCount] = useState(6);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState({});
    const [adopting, setAdopting] = useState(false);

    const launch = async () => {
        if (query.trim().length < 3) return;
        setBusy(true);
        setErr("");
        setResults([]);
        setSelected({});
        try {
            const { data } = await cockpit.post("/prospects/ai-research", {
                query: query.trim(),
                count,
            });
            setResults(data.suggestions || []);
            const sel = {};
            (data.suggestions || []).forEach((_, i) => (sel[i] = true));
            setSelected(sel);
        } catch (e) {
            setErr(
                e?.response?.data?.detail
                || "Recherche assistée indisponible",
            );
        } finally {
            setBusy(false);
        }
    };

    const adopt = async () => {
        const toAdopt = results.filter((_, i) => selected[i]);
        if (toAdopt.length === 0) return;
        setAdopting(true);
        try {
            await cockpit.post("/prospects/ai-research/adopt", {
                suggestions: toAdopt,
            });
            onAdopted();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Adoption impossible");
        } finally {
            setAdopting(false);
        }
    };

    return (
        <div
            data-testid="ai-research-modal"
            className="fixed inset-0 z-50 bg-[#070d18]/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[760px] mt-8 mb-20 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500 z-10"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>
                <div className="px-10 lg:px-14 pt-10 pb-6 border-b border-white/[0.06]">
                    <div className="overline text-[#d4af37]/80 mb-3">
                        <Sparkles
                            strokeWidth={1}
                            size={12}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Recherche assistée
                    </div>
                    <h3
                        className="font-serif text-[24px] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Identifier de nouvelles structures.
                    </h3>
                    <p className="mt-2 text-[12.5px] text-[#f4ecd8]/45 italic font-light">
                        Décrivez ce que vous cherchez (banques privées
                        Monaco, family offices Suisse, cabinets M&A PACA…).
                        Les structures suggérées seront ajoutées avec le
                        statut "Identifié".
                    </p>
                </div>

                <div className="px-10 lg:px-14 py-8 space-y-6">
                    <div className="flex gap-3 items-end">
                        <div className="flex-1">
                            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/75 mb-3">
                                Requête
                            </label>
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="ai-query"
                                onKeyDown={(e) =>
                                    e.key === "Enter" &&
                                    (e.preventDefault(), launch())
                                }
                                placeholder="ex : family offices à Genève spécialisés transmission"
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                            />
                        </div>
                        <div className="w-24">
                            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/75 mb-3">
                                Nb
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={12}
                                value={count}
                                onChange={(e) =>
                                    setCount(Number(e.target.value) || 6)
                                }
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={launch}
                            disabled={busy || query.trim().length < 3}
                            data-testid="ai-launch"
                            className="px-5 py-2.5 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-30"
                        >
                            {busy ? "Recherche…" : "Lancer"}
                        </button>
                    </div>

                    {err && (
                        <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                            {err}
                        </div>
                    )}

                    {results.length > 0 && (
                        <>
                            <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light">
                                Cochez les structures à ajouter à la pépinière.
                            </p>
                            <ul className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                {results.map((s, i) => (
                                    <li
                                        key={i}
                                        data-testid={`ai-result-${i}`}
                                        className="border border-white/[0.07] p-4 flex items-start gap-3"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!selected[i]}
                                            onChange={(e) =>
                                                setSelected((s) => ({
                                                    ...s,
                                                    [i]: e.target.checked,
                                                }))
                                            }
                                            className="mt-1.5 accent-[#d4af37]"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className="font-serif text-[16px] text-[#f4ecd8] leading-tight"
                                                style={{ fontWeight: 400 }}
                                            >
                                                {s.name}
                                            </div>
                                            <div className="mt-1 text-[11px] text-[#d4af37]/70 tracking-wide">
                                                {typeLabel(s.type)}
                                                {s.city || s.country
                                                    ? ` · ${[s.city, s.country].filter(Boolean).join(", ")}`
                                                    : ""}
                                                {s.website && (
                                                    <a
                                                        href={
                                                            s.website.startsWith(
                                                                "http",
                                                            )
                                                                ? s.website
                                                                : `https://${s.website}`
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="ml-3 text-[#f4ecd8]/55 hover:text-[#d4af37] underline"
                                                    >
                                                        {s.website}
                                                    </a>
                                                )}
                                            </div>
                                            {s.description && (
                                                <p className="mt-2 text-[13px] font-serif text-[#f4ecd8]/75 leading-relaxed">
                                                    {s.description}
                                                </p>
                                            )}
                                            {s.rationale && (
                                                <p className="mt-2 text-[12.5px] italic font-light text-[#d4af37]/75 leading-relaxed">
                                                    « {s.rationale} »
                                                </p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={adopt}
                                    disabled={
                                        adopting
                                        || Object.values(selected).every(
                                            (v) => !v,
                                        )
                                    }
                                    data-testid="ai-adopt"
                                    className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-30"
                                >
                                    {adopting
                                        ? "Ajout…"
                                        : "Ajouter à la pépinière"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// Silence unused symbols (kept for future inline icons)
// eslint-disable-next-line no-unused-expressions
[Globe, MapPin, Linkedin];
